import axios from 'axios'

const BASE_URL = process.env.VUE_APP_PERKI_API_URL || 'http://localhost:3000/api/v1'

// Create axios instance for mobile CMS
const mobileCmsAxios = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: false,
})

// Request interceptor - add mobile_cms_token to requests
mobileCmsAxios.interceptors.request.use(
  config => {
    const token = localStorage.getItem('mobile_cms_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => {
    console.error('Mobile CMS Request Interceptor Error:', error)
    return Promise.reject(error)
  },
)

// Response interceptor - handle errors
mobileCmsAxios.interceptors.response.use(
  response => response,
  error => {
    console.error('Mobile CMS API Error:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    })

    if (error.response?.status === 401) {
      // Clear mobile CMS tokens
      localStorage.removeItem('mobile_cms_token')
      localStorage.removeItem('mobile_cms_user')
      localStorage.removeItem('mobile_cms_role')

      // Redirect to mobile-cms login
      const currentPath = window.location.pathname + window.location.search + window.location.hash
      if (!window.location.pathname.startsWith('/mobile-cms/login')) {
        window.location.href = `/mobile-cms/login?redirect=${encodeURIComponent(currentPath)}`
      }
    }

    return Promise.reject(error)
  },
)

// Auth APIs
export const mobileCmsAuthAPI = {
  login: async data => await mobileCmsAxios.post('/mobile/auth/login-cms', data),
}

// Admin Management APIs
export const mobileCmsAdminAPI = {
  getAll: async params => await mobileCmsAxios.get('/mobile/admin-management', { params }),
  getById: async id => await mobileCmsAxios.get(`/mobile/admin-management/${id}`),
  create: async data => await mobileCmsAxios.post('/mobile/admin-management', data),
  update: async (id, data) => await mobileCmsAxios.put(`/mobile/admin-management/${id}`, data),
  delete: async id => await mobileCmsAxios.delete(`/mobile/admin-management/${id}`),
}

// User Management APIs
export const mobileCmsUserAPI = {
  getAll: async params => await mobileCmsAxios.get('/mobile/user-management', { params }),
  getById: async id => await mobileCmsAxios.get(`/mobile/user-management/${id}`),
  update: async (id, data) => await mobileCmsAxios.put(`/mobile/user-management/${id}`, data),
  delete: async id => await mobileCmsAxios.delete(`/mobile/user-management/${id}`),
}

// Banner APIs
export const mobileCmsBannerAPI = {
  getAll: async params => await mobileCmsAxios.get('/mobile/banner', { params }),
  getById: async id => await mobileCmsAxios.get(`/mobile/banner/${id}`),
  create: async formData =>
    await mobileCmsAxios.post('/mobile/banner', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  update: async (id, formData) =>
    await mobileCmsAxios.put(`/mobile/banner/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  delete: async id => await mobileCmsAxios.delete(`/mobile/banner/${id}`),
  reorder: async items => await mobileCmsAxios.patch('/mobile/banner/reorder', items),
}

// Guideline APIs
const ADMIN_GUIDELINE_PREFIX = '/mobile/admin/guidelines'

export const mobileCmsGuidelineAPI = {
  // Sources
  getSources: async params =>
    await mobileCmsAxios.get(`${ADMIN_GUIDELINE_PREFIX}/sources`, { params }),
  createSource: async formData =>
    await mobileCmsAxios.post(`${ADMIN_GUIDELINE_PREFIX}/sources`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  updateSource: async (id, formData) =>
    await mobileCmsAxios.put(`${ADMIN_GUIDELINE_PREFIX}/sources/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  deleteSource: async id => await mobileCmsAxios.delete(`${ADMIN_GUIDELINE_PREFIX}/sources/${id}`),

  // Guidelines
  getAll: async params => await mobileCmsAxios.get(`${ADMIN_GUIDELINE_PREFIX}/`, { params }),
  getById: async id => await mobileCmsAxios.get(`${ADMIN_GUIDELINE_PREFIX}/${id}`),
  create: async formData =>
    await mobileCmsAxios.post(`${ADMIN_GUIDELINE_PREFIX}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  update: async (id, formData) =>
    await mobileCmsAxios.put(`${ADMIN_GUIDELINE_PREFIX}/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  updateStatus: async (id, data) =>
    await mobileCmsAxios.patch(`/mobile/guidelines/${id}/status`, data),
  delete: async id => await mobileCmsAxios.delete(`${ADMIN_GUIDELINE_PREFIX}/${id}`),

  // Categories
  getCategories: async guidelineId =>
    await mobileCmsAxios.get(`${ADMIN_GUIDELINE_PREFIX}/${guidelineId}/categories`),
  createCategory: async data =>
    await mobileCmsAxios.post(`${ADMIN_GUIDELINE_PREFIX}/categories`, data),
  updateCategory: async (id, data) =>
    await mobileCmsAxios.put(`${ADMIN_GUIDELINE_PREFIX}/categories/${id}`, data),
  reorderCategories: async data =>
    await mobileCmsAxios.put(`${ADMIN_GUIDELINE_PREFIX}/categories/reorder`, data),
  deleteCategory: async id =>
    await mobileCmsAxios.delete(`${ADMIN_GUIDELINE_PREFIX}/categories/${id}`),
  getCategoryById: async id =>
    await mobileCmsAxios.get(`${ADMIN_GUIDELINE_PREFIX}/categories/${id}`),

  // Diagnoses
  createDiagnosis: async data =>
    await mobileCmsAxios.post(`${ADMIN_GUIDELINE_PREFIX}/diagnoses`, data),
  updateDiagnosis: async (id, data) =>
    await mobileCmsAxios.put(`${ADMIN_GUIDELINE_PREFIX}/diagnoses/${id}`, data),
  deleteDiagnosis: async id =>
    await mobileCmsAxios.delete(`${ADMIN_GUIDELINE_PREFIX}/diagnoses/${id}`),
  getDiagnosisById: async id =>
    await mobileCmsAxios.get(`${ADMIN_GUIDELINE_PREFIX}/diagnoses/${id}`),

  // ICD Codes
  addICDCode: async (diagnosisId, data) =>
    await mobileCmsAxios.post(`${ADMIN_GUIDELINE_PREFIX}/diagnoses/${diagnosisId}/icd`, data),
  removeICDCode: async id =>
    await mobileCmsAxios.delete(`${ADMIN_GUIDELINE_PREFIX}/diagnoses/icd/${id}`),

  // Sections
  getSectionById: async id => await mobileCmsAxios.get(`${ADMIN_GUIDELINE_PREFIX}/sections/${id}`),
  createSection: async (diagnosisId, data) =>
    await mobileCmsAxios.post(`${ADMIN_GUIDELINE_PREFIX}/diagnoses/${diagnosisId}/sections`, data),
  updateSection: async (id, data) =>
    await mobileCmsAxios.put(`${ADMIN_GUIDELINE_PREFIX}/sections/${id}`, data),
  reorderSections: async (diagnosisId, data) =>
    await mobileCmsAxios.put(
      `${ADMIN_GUIDELINE_PREFIX}/diagnoses/${diagnosisId}/sections/reorder`,
      data,
    ),
  deleteSection: async id =>
    await mobileCmsAxios.delete(`${ADMIN_GUIDELINE_PREFIX}/sections/${id}`),
  getAlurAlgoritmaKlinisOptions: async params =>
    await mobileCmsAxios.get('/mobile/algoritma-klinis', { params }),
  getCalculatorOptions: async params => await mobileCmsAxios.get('/mobile/calculators/', { params }),

  // Editor
  uploadEditorImage: async formData =>
    await mobileCmsAxios.post(`${ADMIN_GUIDELINE_PREFIX}/editor/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
}

export const mobileCmsAlgoritmaAPI = {
  // Algoritma (header) endpoints
  getAll: async params => await mobileCmsAxios.get('/mobile/algoritma-klinis', { params }),
  getById: async id => await mobileCmsAxios.get(`/mobile/algoritma-klinis/${id}`),
  create: async formData =>
    await mobileCmsAxios.post('/mobile/algoritma-klinis', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  update: async (id, formData) =>
    await mobileCmsAxios.put(`/mobile/algoritma-klinis/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  delete: async id => await mobileCmsAxios.delete(`/mobile/algoritma-klinis/${id}`),
  updateStatus: async (id, data) =>
    await mobileCmsAxios.patch(`/mobile/algoritma-klinis/${id}/status`, data),

  // Node endpoints
  createNode: async data => await mobileCmsAxios.post('/mobile/algoritma-klinis/node', data),
  updateNode: async (id, data) =>
    await mobileCmsAxios.put(`/mobile/algoritma-klinis/node/${id}`, data),
  deleteNode: async id => await mobileCmsAxios.delete(`/mobile/algoritma-klinis/node/${id}`),
}

// Member PERKI APIs (CMS)
export const mobileCmsMemberAPI = {
  getLinked: async params => await mobileCmsAxios.get('/cms/members/linked', { params }),
  getById: async id => await mobileCmsAxios.get(`/cms/members/${id}`),
  update: async (id, data) => await mobileCmsAxios.patch(`/cms/members/${id}`, data),
  uploadAvatar: async (id, formData) =>
    await mobileCmsAxios.post(`/cms/members/${id}/avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  toggleActive: async id => await mobileCmsAxios.patch(`/cms/members/${id}/toggle-active`),
}

// Member Application APIs
export const mobileCmsMemberApplicationAPI = {
  getAll: async params => await mobileCmsAxios.get('/membership-applications/', { params }),
  getStatusSummary: async () => await mobileCmsAxios.get('/membership-applications/status-summary'),
  getMemberDistribution: async () =>
    await mobileCmsAxios.get('/membership-applications/member-distribution'),
  getMembershipTrend: async params =>
    await mobileCmsAxios.get('/membership-applications/trend', { params }),
  getById: async id => await mobileCmsAxios.get(`/membership-applications/${id}`),
  approve: async id => await mobileCmsAxios.put(`/membership-applications/${id}/approve`),
  reject: async (id, data) =>
    await mobileCmsAxios.put(`/membership-applications/${id}/reject`, data),
  revision: async (id, data) =>
    await mobileCmsAxios.put(`/membership-applications/${id}/revision`, data),
}

export default {
  auth: mobileCmsAuthAPI,
  admin: mobileCmsAdminAPI,
  user: mobileCmsUserAPI,
  banner: mobileCmsBannerAPI,
  guideline: mobileCmsGuidelineAPI,
  algoritma: mobileCmsAlgoritmaAPI,
  memberApplication: mobileCmsMemberApplicationAPI,
}
